import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { stripePromise } from "../../../stripe";
import logoSm from "../../../assets/images/logo-sm.png";
import selectedRadio from "../../../assets/images/selected-radio.png";
import unSelectedDarkRadio from "../../../assets/images/unselected-dark-radio.png";
import visaCard from "../../../assets/images/visa.png";
import masterCard from "../../../assets/images/master-card.png";
import discover from "../../../assets/images/discover.png";
import maestro from "../../../assets/images/maestro.png";
import light from "../../../assets/images/light.png";
import infoIcon from "../../../assets/images/info-icon.png";
import StripeIcon from "../../../assets/icons/stripe.svg";
import axios from "../../../axios-instance";
import clsx from "clsx";
import Loader from "../../../components/Loader/Loader";
import { getAdminBusinessId } from "../../../utils/authHelper";

const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      fontSize: "16px",
      color: "#32325d",
      fontFamily: "Inter, Arial, sans-serif",
      "::placeholder": {
        color: "#a0aec0",
      },
    },
    invalid: {
      color: "#e53e3e",
    },
  },
};

const PaymentForm = () => {
  const [pageLoading, setPageLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(null);
  const [planId, setPlanId] = useState("");
  const [planType, setPlanType] = useState("monthly");
  const [teamStatus, setTeamStatus] = useState(false);
  const [plans, setPlans] = useState([]);
  const [cardError, setCardError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    setPageLoading(true);
    axios
      .get("/admin/plans")
      .then((response) => {
        setPageLoading(false);
        setPlans(response.data);
      })
      .catch((error) => {
        setPageLoading(false);
        console.error(error);
      });
  }, []);

  const selectedPlan = plans.find((p) => p.id === +planId);
  const isFreePlan = selectedPlan && +selectedPlan.monthly_price === 0;

  const getTotalAmount = () => {
    if (!selectedPlan) return 0;
    return planType === "monthly"
      ? selectedPlan.monthly_price
      : selectedPlan.annual_price;
  };

  const isAnnual = planType === "annual";

  const handleFreePlanSubmit = () => {
    navigate("/signup/thanksForPayment");
  };

  const handlePaidSubmit = async (e) => {
    e.preventDefault();
    setCardError(null);

    if (!planId) {
      setCardError("Please select a plan.");
      return;
    }

    if (isFreePlan && !teamStatus) {
      handleFreePlanSubmit();
      return;
    }

    if (!stripe || !elements) return;

    setProcessing(true);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardElement),
    });

    if (error) {
      setCardError(error.message);
      setProcessing(false);
      return;
    }

    try {
      const businessId = getAdminBusinessId();
      const res = await axios.post("/admin/subscription/create", {
        payment_method_id: paymentMethod.id,
        plan_id: selectedPlan.id,
        plan_type: planType,
        business_id: businessId,
      });

      if (res.data.success) {
        const result = await stripe.confirmCardPayment(res.data.client_secret, {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        });

        if (result.error) {
          setCardError(result.error.message);
          setProcessing(false);
          return;
        }

        if (result.paymentIntent.status === "succeeded") {
          setLoadingText("Please wait, your subscription is being activated...");
          setPageLoading(true);

          try {
            await axios.post("/admin/subscription/confirm", {
              subscription_id: res.data.subscription_id,
              business_id: businessId,
            });
          } catch (confirmErr) {
            console.error("Subscription confirm error", confirmErr);
          }

          setPageLoading(false);
          setProcessing(false);
          navigate("/signup/thanksForPayment");
        }
      } else {
        setCardError(res.data.message || "Error creating subscription.");
        setProcessing(false);
      }
    } catch (err) {
      console.error(err);
      setCardError(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
      setProcessing(false);
    }
  };

  return (
    <>
      <Loader isShowLoader={pageLoading} loadingText={loadingText} />

      <div>
        <header>
          <div className="header-container">
            <div className="logo">
              <img src={logoSm} alt="" />
            </div>
          </div>
        </header>

        <div className="payment-section">
          <div>
            <form onSubmit={handlePaidSubmit} className="payment">
              <div className="payment-method">
                <h1 className="payment-heading">Payment Method</h1>

                {!(isFreePlan && !teamStatus) && (
                  <div className="card-details">
                    <div className="d-flex align-items-center mb-2">
                      <span className="cards-icons">
                        <div className="icon">
                          <img src={visaCard} alt="Visa" />
                        </div>
                        <div className="icon">
                          <img src={masterCard} alt="Mastercard" />
                        </div>
                        <div className="icon">
                          <img src={discover} alt="Discover" />
                        </div>
                        <div className="icon">
                          <img src={maestro} alt="Maestro" />
                        </div>
                      </span>
                      {StripeIcon && (
                        <img
                          src={StripeIcon}
                          alt="Powered by Stripe"
                          style={{ width: "20px", marginLeft: "8px" }}
                        />
                      )}
                    </div>
                    <div className="stripe-card-wrapper mb-3" style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      padding: "12px",
                    }}>
                      <CardElement options={CARD_ELEMENT_OPTIONS} />
                    </div>
                    {cardError && (
                      <p className="text-danger mt-2" style={{ fontSize: "14px" }}>
                        {cardError}
                      </p>
                    )}
                  </div>
                )}

                {isFreePlan && !teamStatus && (
                  <p className="mt-3" style={{ color: "#718096" }}>
                    No payment required for the free plan.
                  </p>
                )}
              </div>

              <div className="payment-order">
                <h1 className="payment-heading">Order Summary</h1>
                <div className="toggle-section">
                  <p>Individual Plans</p>
                  <label className="switch">
                    <input
                      name="team"
                      checked={teamStatus}
                      onChange={(event) => {
                        setTeamStatus(event.target.checked);
                        setPlanId("");
                      }}
                      type="checkbox"
                    />
                    <span className="slider round" />
                  </label>
                  <p>Team</p>
                </div>
                <div className="payment-details">
                  <div className="payment-details-btns">
                    <button
                      type="button"
                      className={clsx({ active: planType === "monthly" })}
                      onClick={() => setPlanType("monthly")}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      className={clsx({ active: planType === "annual" })}
                      onClick={() => setPlanType("annual")}
                    >
                      Annually
                    </button>
                  </div>
                  <div className="payment-details-heading">
                    <img src={light} alt="" />
                    <h1>You Can Save $50 On Annually Subscription</h1>
                  </div>

                  <div className="radios">
                    {plans
                      .filter((plan) => {
                        return (
                          plan.plan_type ===
                          (teamStatus ? "team" : "individual")
                        );
                      })
                      .map((plan) => {
                        return (
                          <label className="tag-button-label" key={plan.id}>
                            <input
                              type="radio"
                              value={plan.id}
                              checked={planId === String(plan.id)}
                              onChange={(event) => {
                                setPlanId(event.target.value);
                              }}
                              name="plan_id"
                            />
                            <div className="tag-button">
                              <div className="tag-icon checked">
                                <img src={selectedRadio} alt="" />
                              </div>
                              <div className="tag-icon unchecked">
                                <img src={unSelectedDarkRadio} alt="" />
                              </div>
                              <div className="tag-button-texts">
                                <h1>
                                  {plan.plan_name} Account
                                  {planType === "monthly" && <> (Monthly)</>}
                                  {isAnnual && <> (Annually)</>}
                                  <img src={infoIcon} alt="" />
                                </h1>
                                <p>
                                  Start automating basic scheduling features
                                </p>
                              </div>
                            </div>
                            {planType === "monthly" && (
                              <span className="amount">
                                <p>${plan.monthly_price}.00</p>
                              </span>
                            )}
                            {isAnnual && (
                              <span className="amount">
                                <p>${plan.annual_price}.00</p>
                              </span>
                            )}
                          </label>
                        );
                      })}
                  </div>
                  <div className="coupon-code">
                    <div className="form-group">
                      <label className="same-label" htmlFor="couponCode">
                        Have a coupon code?
                      </label>
                      <div className="coupon-code-input">
                        <input
                          type="text"
                          className="form-control"
                          name="couponCode"
                          id="couponCode"
                        />
                        <button className="btn btn-app" type="button">
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="submit-section">
                    <div className="total">
                      <div>
                        <h1>Total</h1>
                        <p>Next Payment (date) after 30 days</p>
                      </div>
                      <div>
                        <p className="total-amount">${getTotalAmount()}</p>
                      </div>
                    </div>
                    <div className="submit-btn">
                      <button
                        className="btn btn-app"
                        type="submit"
                        disabled={processing || !planId}
                      >
                        {processing
                          ? "Processing..."
                          : isFreePlan && !teamStatus
                          ? "Finish"
                          : "Buy Now"}
                      </button>
                    </div>
                    <div className="query">
                      <p>
                        Questions? Send us an email at{" "}
                        <a href="#">sales@meetocto.com</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        <footer>
          <div className="footer-container">
            <ul className="languages d-none">
              <li>English (UK)</li>
              <li>हिन्दी</li>
              <li>ਪੰਜਾਬੀ</li>
              <li>বাংলা</li>
              <li>Deutsch</li>
              <li>ગુજરાতી</li>
              <li>Español</li>
            </ul>
            <p>Copyright © 2021 MeetOcto.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

const Payment = () => {
  return (
    <Elements
      stripe={stripePromise}
      options={{ appearance: { theme: "stripe" } }}
    >
      <PaymentForm />
    </Elements>
  );
};

export default Payment;
